/**
 * EventPropertyPanel - Dynamic property editor for cutscene events
 */

import React, { useCallback, useMemo } from "react";
import { Direction, DirectionArray } from "types/battle-engine/battle";
import {
  CutsceneData,
  CutsceneEvent,
  EasingType,
  CutsceneActor,
} from "@/lib/cutscene/types";
import { getEventTypeName, updateEvent } from "@/lib/cutscene/utils";

interface EventPropertyPanelProps {
  cutscene: CutsceneData;
  onCutsceneChange: (cutscene: CutsceneData) => void;
  selectedEventId: string | null;
  actors: Record<string, CutsceneActor>;
  onOpenAssetBrowser: (type: "map" | "vfx" | "pokemon") => void;
}

const EASING_OPTIONS: EasingType[] = [
  "linear",
  "easeIn",
  "easeOut",
  "easeInOut",
  "easeInQuad",
  "easeOutQuad",
  "easeInOutQuad",
  "easeInCubic",
  "easeOutCubic",
  "easeInOutCubic",
];

const ANIMATION_OPTIONS = [
  "Idle",
  "Walk",
  "Attack",
  "Hurt",
  "Sleep",
  "Charge",
  "Swing",
  "Special",
  "Rotate",
];

export const EventPropertyPanel: React.FC<EventPropertyPanelProps> = ({
  cutscene,
  onCutsceneChange,
  selectedEventId,
  actors,
  onOpenAssetBrowser,
}) => {
  const selectedEvent = useMemo(() => {
    if (!selectedEventId) return null;
    return cutscene.events.find((e) => e.id === selectedEventId) || null;
  }, [cutscene.events, selectedEventId]);

  const selectedEventIndex = useMemo(() => {
    if (!selectedEventId) return -1;
    return cutscene.events.findIndex((e) => e.id === selectedEventId);
  }, [cutscene.events, selectedEventId]);

  const handleUpdate = useCallback(
    (updates: Partial<CutsceneEvent>) => {
      if (selectedEventIndex < 0) return;
      const newCutscene = updateEvent(cutscene, selectedEventIndex, updates);
      onCutsceneChange(newCutscene);
    },
    [cutscene, selectedEventIndex, onCutsceneChange]
  );

  const actorIds = useMemo(() => Object.keys(actors), [actors]);

  // Also get actor IDs from spawn events in the cutscene
  const allActorIds = useMemo(() => {
    const ids = new Set(actorIds);
    cutscene.events.forEach((event) => {
      if (event.type === "spawn_pokemon") {
        ids.add(event.actorId);
      }
    });
    return Array.from(ids);
  }, [actorIds, cutscene.events]);

  const vfxIds = useMemo(() => {
    const ids = new Set<string>();
    cutscene.events.forEach((event) => {
      if (event.type === "play_vfx") {
        ids.add(event.vfxId);
      }
    });
    return Array.from(ids);
  }, [cutscene.events]);

  if (!selectedEvent) {
    return (
      <div className="h-full bg-[#1a1f2e] border-t border-gray-700 flex items-center justify-center text-gray-500 text-sm">
        Select an event to edit its properties
      </div>
    );
  }

  return (
    <div className="h-full bg-[#1a1f2e] border-t border-gray-700 overflow-y-auto">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-200">
            {getEventTypeName(selectedEvent.type)}
          </h3>
          <span className="text-xs text-gray-500">ID: {selectedEvent.id}</span>
        </div>

        {/* Common Properties */}
        <div className="mb-4 pb-4 border-b border-gray-700">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={selectedEvent.blocking}
              onChange={(e) => handleUpdate({ blocking: e.target.checked })}
              className="rounded bg-gray-700 border-gray-600"
            />
            Blocking
            <span className="text-xs text-gray-500">
              (wait for completion before next event)
            </span>
          </label>
        </div>

        {/* Type-specific Properties */}
        <div className="space-y-4">
          {renderEventProperties(
            selectedEvent,
            handleUpdate,
            allActorIds,
            vfxIds,
            onOpenAssetBrowser
          )}
        </div>
      </div>
    </div>
  );
};

function renderEventProperties(
  event: CutsceneEvent,
  onUpdate: (updates: Partial<CutsceneEvent>) => void,
  actorIds: string[],
  vfxIds: string[],
  onOpenAssetBrowser: (type: "map" | "vfx" | "pokemon") => void
): React.ReactNode {
  switch (event.type) {
    case "spawn_pokemon":
      return (
        <>
          <InputField
            label="Actor ID"
            value={event.actorId}
            onChange={(value) => onUpdate({ actorId: value })}
          />
          <div className="flex items-end gap-2">
            <InputField
              label="Pokemon Name"
              value={event.pokemonName}
              onChange={(value) => onUpdate({ pokemonName: value })}
              className="flex-1"
            />
            <button
              onClick={() => onOpenAssetBrowser("pokemon")}
              className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded"
            >
              Browse
            </button>
          </div>
          <PositionField
            label="Position"
            value={event.position}
            onChange={(position) => onUpdate({ position })}
          />
          <SelectField
            label="Direction"
            value={event.direction}
            options={DirectionArray.map((d) => ({ value: d, label: d }))}
            onChange={(value) => onUpdate({ direction: value as Direction })}
          />
          <SelectField
            label="Animation"
            value={event.animation}
            options={ANIMATION_OPTIONS.map((a) => ({ value: a, label: a }))}
            onChange={(value) => onUpdate({ animation: value })}
          />
          <NumberField
            label="Scale"
            value={event.scale || 1}
            onChange={(value) => onUpdate({ scale: value })}
            step={0.1}
            min={0.1}
            max={5}
          />
        </>
      );

    case "move_pokemon":
      return (
        <>
          <SelectField
            label="Actor ID"
            value={event.actorId}
            options={[
              ...(event.actorId === "" ? [{ value: "", label: "(select actor)" }] : []),
              ...actorIds.map((id) => ({ value: id, label: id })),
            ]}
            onChange={(value) => onUpdate({ actorId: value })}
          />
          <PositionField
            label="Target Position"
            value={event.position}
            onChange={(position) => onUpdate({ position })}
          />
          <NumberField
            label="Duration (ms)"
            value={event.duration}
            onChange={(value) => onUpdate({ duration: value })}
            min={0}
            step={100}
          />
          <SelectField
            label="Easing"
            value={event.easing || "linear"}
            options={EASING_OPTIONS.map((e) => ({ value: e, label: e }))}
            onChange={(value) => onUpdate({ easing: value as EasingType })}
          />
          <SelectField
            label="Animation"
            value={event.animation || "Walk"}
            options={ANIMATION_OPTIONS.map((a) => ({ value: a, label: a }))}
            onChange={(value) => onUpdate({ animation: value })}
          />
        </>
      );

    case "animate_pokemon":
      return (
        <>
          <SelectField
            label="Actor ID"
            value={event.actorId}
            options={[
              ...(event.actorId === "" ? [{ value: "", label: "(select actor)" }] : []),
              ...actorIds.map((id) => ({ value: id, label: id })),
            ]}
            onChange={(value) => onUpdate({ actorId: value })}
          />
          <SelectField
            label="Animation"
            value={event.animation}
            options={ANIMATION_OPTIONS.map((a) => ({ value: a, label: a }))}
            onChange={(value) => onUpdate({ animation: value })}
          />
          <SelectField
            label="Direction"
            value={event.direction || ""}
            options={[
              { value: "", label: "(unchanged)" },
              ...DirectionArray.map((d) => ({ value: d, label: d })),
            ]}
            onChange={(value) =>
              onUpdate({ direction: value ? (value as Direction) : undefined })
            }
          />
          <NumberField
            label="Duration (ms)"
            value={event.duration || 0}
            onChange={(value) => onUpdate({ duration: value || undefined })}
            min={0}
            step={100}
            placeholder="0 = until next animation"
          />
        </>
      );

    case "remove_pokemon":
      return (
        <>
          <SelectField
            label="Actor ID"
            value={event.actorId}
            options={[
              ...(event.actorId === "" ? [{ value: "", label: "(select actor)" }] : []),
              ...actorIds.map((id) => ({ value: id, label: id })),
            ]}
            onChange={(value) => onUpdate({ actorId: value })}
          />
          <NumberField
            label="Fade Out Duration (ms)"
            value={event.fadeOutDuration || 0}
            onChange={(value) => onUpdate({ fadeOutDuration: value })}
            min={0}
            step={100}
          />
        </>
      );

    case "play_vfx":
      return (
        <>
          <InputField
            label="VFX ID"
            value={event.vfxId}
            onChange={(value) => onUpdate({ vfxId: value })}
          />
          <div className="flex items-end gap-2">
            <InputField
              label="Sprite Path"
              value={event.spritePath}
              onChange={(value) => onUpdate({ spritePath: value })}
              className="flex-1"
            />
            <button
              onClick={() => onOpenAssetBrowser("vfx")}
              className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded"
            >
              Browse
            </button>
          </div>
          <PositionField
            label="Position"
            value={event.position}
            onChange={(position) => onUpdate({ position })}
          />
          <SelectField
            label="Anchor to Actor"
            value={event.anchorToActor || ""}
            options={[
              { value: "", label: "(none)" },
              ...actorIds.map((id) => ({ value: id, label: id })),
            ]}
            onChange={(value) =>
              onUpdate({ anchorToActor: value || undefined })
            }
          />
          <NumberField
            label="Scale"
            value={event.scale || 1}
            onChange={(value) => onUpdate({ scale: value })}
            step={0.1}
            min={0.1}
          />
          <NumberField
            label="Alpha"
            value={event.alpha || 1}
            onChange={(value) => onUpdate({ alpha: value })}
            step={0.1}
            min={0}
            max={1}
          />
          <NumberField
            label="Loops"
            value={event.loops || 1}
            onChange={(value) => onUpdate({ loops: value })}
            min={0}
            step={1}
            placeholder="0 = infinite"
          />
          <NumberField
            label="Speed"
            value={event.speed || 1}
            onChange={(value) => onUpdate({ speed: value })}
            step={0.1}
            min={0.1}
          />
          <NumberField
            label="Y Offset"
            value={event.yOffset || 0}
            onChange={(value) => onUpdate({ yOffset: value })}
            step={10}
          />
        </>
      );

    case "stop_vfx":
      return (
        <>
          <SelectField
            label="VFX ID"
            value={event.vfxId}
            options={vfxIds.map((id) => ({ value: id, label: id }))}
            onChange={(value) => onUpdate({ vfxId: value })}
          />
          <NumberField
            label="Fade Out Duration (ms)"
            value={event.fadeOutDuration || 0}
            onChange={(value) => onUpdate({ fadeOutDuration: value })}
            min={0}
            step={100}
          />
        </>
      );

    case "camera_pan":
      return (
        <>
          <PositionField
            label="Target Position"
            value={event.target}
            onChange={(target) => onUpdate({ target })}
          />
          <NumberField
            label="Duration (ms)"
            value={event.duration}
            onChange={(value) => onUpdate({ duration: value })}
            min={0}
            step={100}
          />
          <SelectField
            label="Easing"
            value={event.easing || "linear"}
            options={EASING_OPTIONS.map((e) => ({ value: e, label: e }))}
            onChange={(value) => onUpdate({ easing: value as EasingType })}
          />
        </>
      );

    case "camera_zoom":
      return (
        <>
          <NumberField
            label="Zoom Level"
            value={event.zoom}
            onChange={(value) => onUpdate({ zoom: value })}
            step={0.1}
            min={0.1}
            max={5}
          />
          <NumberField
            label="Duration (ms)"
            value={event.duration}
            onChange={(value) => onUpdate({ duration: value })}
            min={0}
            step={100}
          />
          <SelectField
            label="Easing"
            value={event.easing || "linear"}
            options={EASING_OPTIONS.map((e) => ({ value: e, label: e }))}
            onChange={(value) => onUpdate({ easing: value as EasingType })}
          />
        </>
      );

    case "camera_shake":
      return (
        <>
          <NumberField
            label="Intensity"
            value={event.intensity}
            onChange={(value) => onUpdate({ intensity: value })}
            step={0.1}
            min={0.1}
            max={5}
          />
          <NumberField
            label="Duration (ms)"
            value={event.duration}
            onChange={(value) => onUpdate({ duration: value })}
            min={50}
            step={50}
          />
        </>
      );

    case "dialog":
      return (
        <>
          <InputField
            label="Speaker"
            value={event.speaker}
            onChange={(value) => onUpdate({ speaker: value })}
          />
          <InputField
            label="Portrait Path"
            value={event.portrait || ""}
            onChange={(value) => onUpdate({ portrait: value || undefined })}
            placeholder="(optional)"
          />
          <TextAreaField
            label="Text"
            value={event.text}
            onChange={(value) => onUpdate({ text: value })}
            rows={3}
          />
          <NumberField
            label="Duration (ms)"
            value={event.duration}
            onChange={(value) => onUpdate({ duration: value })}
            min={0}
            step={500}
          />
          <NumberField
            label="Type Speed (chars/sec)"
            value={event.typeSpeed || 0}
            onChange={(value) => onUpdate({ typeSpeed: value || undefined })}
            min={0}
            step={10}
            placeholder="0 = instant"
          />
        </>
      );

    case "hide_dialog":
      return (
        <>
          <NumberField
            label="Duration (ms)"
            value={event.duration || 0}
            onChange={(value) => onUpdate({ duration: value })}
            min={0}
            step={100}
          />
        </>
      );

    case "wait":
      return (
        <>
          <NumberField
            label="Duration (ms)"
            value={event.duration}
            onChange={(value) => onUpdate({ duration: value })}
            min={0}
            step={100}
          />
        </>
      );

    case "fade":
      return (
        <>
          <SelectField
            label="Direction"
            value={event.direction}
            options={[
              { value: "in", label: "Fade In (to transparent)" },
              { value: "out", label: "Fade Out (to color)" },
            ]}
            onChange={(value) =>
              onUpdate({ direction: value as "in" | "out" })
            }
          />
          <InputField
            label="Color"
            value={event.color || "#000000"}
            onChange={(value) => onUpdate({ color: value })}
            type="color"
          />
          <NumberField
            label="Duration (ms)"
            value={event.duration}
            onChange={(value) => onUpdate({ duration: value })}
            min={0}
            step={100}
          />
        </>
      );

    default:
      return <div className="text-gray-500 text-sm">Unknown event type</div>;
  }
}

// ============================================================================
// Field Components
// ============================================================================

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  className?: string;
}

function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  className = "",
}: InputFieldProps) {
  return (
    <div className={className}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded focus:border-blue-500 focus:outline-none text-white"
      />
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
}: NumberFieldProps) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded focus:border-blue-500 focus:outline-none text-white"
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

function SelectField({ label, value, options, onChange }: SelectFieldProps) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded focus:border-blue-500 focus:outline-none text-white"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}

function TextAreaField({
  label,
  value,
  onChange,
  rows = 3,
}: TextAreaFieldProps) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded focus:border-blue-500 focus:outline-none text-white resize-none"
      />
    </div>
  );
}

interface PositionFieldProps {
  label: string;
  value: { x: number; y: number };
  onChange: (value: { x: number; y: number }) => void;
}

function PositionField({ label, value, onChange }: PositionFieldProps) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <div className="flex gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500 w-4">X</span>
            <input
              type="number"
              value={value.x}
              onChange={(e) =>
                onChange({ ...value, x: parseFloat(e.target.value) || 0 })
              }
              className="w-full px-2 py-1.5 text-sm bg-gray-800 border border-gray-600 rounded focus:border-blue-500 focus:outline-none text-white"
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500 w-4">Y</span>
            <input
              type="number"
              value={value.y}
              onChange={(e) =>
                onChange({ ...value, y: parseFloat(e.target.value) || 0 })
              }
              className="w-full px-2 py-1.5 text-sm bg-gray-800 border border-gray-600 rounded focus:border-blue-500 focus:outline-none text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

